import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Language } from '@/types/multilingual'

// 分类返回类型
export interface MultilingualCategoryItem {
  id: string
  icon: string | null
  sort_order: number
  created_at: string
  updated_at: string
  // 翻译字段
  title: string
  description: string
}

// 获取多语言分类列表
export function useMultilingualCategories(language: Language = 'zh') {
  return useQuery({
    queryKey: ['multilingual-categories', language],
    queryFn: async () => {
      // 获取分类
      const { data: categories, error: catError } = await supabase
        .from('project_categories')
        .select('*')
        .order('sort_order', { ascending: true })
      
      if (catError) throw catError
      if (!categories || categories.length === 0) return []
      
      // 获取对应语言的翻译
      const categoryIds = categories.map(c => c.id)
      const { data: translations, error: transError } = await supabase
        .from('category_translations')
        .select('*')
        .in('category_id', categoryIds)
        .eq('language', language)
      
      if (transError) throw transError
      
      // 创建翻译映射
      const translationMap = new Map(
        translations?.map(t => [t.category_id, t]) || []
      )
      
      // 合并数据，优先使用翻译，回退到原始数据
      return categories.map(category => {
        const trans = translationMap.get(category.id)
        return {
          id: category.id,
          icon: category.icon,
          sort_order: category.sort_order || 0,
          created_at: category.created_at,
          updated_at: category.updated_at,
          title: trans?.title || category.title,
          description: trans?.description || category.description
        } as MultilingualCategoryItem
      })
    }
  })
}

// 获取单个多语言分类
export function useMultilingualCategory(categoryId: string, language: Language = 'zh') {
  return useQuery({
    queryKey: ['multilingual-category', categoryId, language],
    queryFn: async () => {
      // 获取分类
      const { data: category, error: catError } = await supabase
        .from('project_categories')
        .select('*')
        .eq('id', categoryId)
        .maybeSingle()
      
      if (catError) throw catError
      if (!category) return null
      
      // 获取翻译
      const { data: trans, error: transError } = await supabase
        .from('category_translations')
        .select('*')
        .eq('category_id', categoryId)
        .eq('language', language)
        .maybeSingle()
      
      if (transError) throw transError
      
      return {
        id: category.id,
        icon: category.icon,
        sort_order: category.sort_order || 0,
        created_at: category.created_at,
        updated_at: category.updated_at,
        title: trans?.title || category.title,
        description: trans?.description || category.description
      } as MultilingualCategoryItem
    },
    enabled: !!categoryId
  })
}
